import {
  type BaseSelectProps,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { XIcon } from '@phosphor-icons/react';
import { omit } from 'lodash';
import { type ReactNode, useId } from 'react';

import {
  FormField,
  type RequireLabelOrAria,
  Text,
  getLabelOrAriaProps,
  isDefined,
  isTruthy,
} from 'common';

import {
  type BlockFieldMode,
  BlockValueTemplateFieldWrapper,
} from './CodeMirror/FlowTemplateFieldWrapper';
import { type ConfigFieldProps } from './types';
import { useSelectedNodeField } from './useSelectedNodeField';
import { useReactFlowViewContext } from '../../context/context';

interface SelectFieldOption {
  label: ReactNode;
  value: string;
  title?: string;
  disabled?: boolean;
  subHeader?: string;
}

interface InnerSelectFieldProps<TValue extends string>
  extends
    Omit<BaseSelectProps<TValue>, 'variant' | 'children' | 'label' | 'placeholder'>,
    ConfigFieldProps {
  'aria-label': string;
  options: SelectFieldOption[];
  variant?: 'outlined' | 'borderless';
  clearable?: boolean;
  onClear?: () => void;
  defaultValue?: TValue;
  borderRadius?: string;
}

export function BlockInnerSelectField<TValue extends string>(props: InnerSelectFieldProps<TValue>) {
  const {
    fieldName,
    options,
    variant,
    clearable = true,
    onClear,
    defaultValue,
    borderRadius,
    ...fieldProps
  } = props;
  const { field, isFieldErrorVisible, setValue } = useSelectedNodeField<TValue | undefined>(
    fieldName
  );
  const { isEditable } = useReactFlowViewContext();
  return (
    <Select
      {...fieldProps}
      fullWidth
      onChange={(event) => {
        field.onChange(event);
        fieldProps.onChange?.(event, null);
      }}
      {...omit(field, 'onChange')}
      disabled={!isEditable || fieldProps.disabled}
      error={isFieldErrorVisible}
      variant={variant === 'borderless' ? 'standard' : 'outlined'}
      {...(variant === 'borderless' ? { disableUnderline: true } : undefined)}
      value={field.value ?? defaultValue ?? ''}
      endAdornment={
        clearable && isTruthy(field.value) ? (
          <InputAdornment position="end">
            <IconButton
              sx={{ minWidth: 0, padding: 0.5, marginRight: 0.5 }}
              onClick={() => {
                setValue(undefined);
                onClear?.();
              }}
              aria-label="Clear Value"
              disabled={!isEditable || fieldProps.disabled}
              size={fieldProps.size === 'small' ? 'small' : 'medium'}
            >
              <XIcon size={fieldProps.size === 'small' ? 20 : 24} />
            </IconButton>
          </InputAdornment>
        ) : null
      }
      sx={{
        '& .MuiSelect-iconOutlined': {
          display: clearable && field.value ? 'none' : 'block',
        },
        '& .MuiSelect-iconStandard': {
          display: clearable && field.value ? 'none' : 'block',
        },
        borderRadius: borderRadius ?? 1,
      }}
    >
      {options.map((option) => (
        <MenuItem
          key={option.value}
          value={option.value}
          title={option.title}
          disabled={option.disabled}
        >
          <Stack direction="column" alignContent="flex-start">
            {option.label}
            {isDefined(option.subHeader) && <Text variant="subtitle2">{option.subHeader}</Text>}
          </Stack>
        </MenuItem>
      ))}
    </Select>
  );
}

type FieldProps<TValue extends string> = Omit<
  BaseSelectProps<TValue>,
  'variant' | 'children' | 'placeholder'
> &
  ConfigFieldProps & {
    helperText?: ReactNode;
    labelAdornment?: ReactNode;
    clearable?: boolean;
    onClear?: () => void;
    errorMessage?: string;
  } & RequireLabelOrAria;

type SelectFieldProps<TValue extends string> = FieldProps<TValue> & {
  options: SelectFieldOption[];
  fieldVariant?: 'Select' | 'SelectOrBlockValueTemplate';
  onSelectOrBlockValueTemplateModeChange?: (mode: BlockFieldMode) => void;
  defaultValue?: TValue;
};

export function BlockSelectField<TValue extends string>(props: SelectFieldProps<TValue>) {
  const {
    fieldName,
    label,
    'aria-label': ariaLabel,
    helperText,
    labelAdornment,
    fieldVariant,
    onSelectOrBlockValueTemplateModeChange,
    errorMessage,
    ...fieldProps
  } = props;
  const { visibleError } = useSelectedNodeField<TValue>(fieldName);
  const fieldId = useId();
  const ariaLabelString = ariaLabel ?? (typeof label === 'string' ? label : 'Value');

  const innerSelectFieldProps = {
    fieldName,
    ...fieldProps,
    id: fieldId,
    ['aria-label']: ariaLabelString,
  };
  return (
    <FormField
      id={fieldId}
      {...getLabelOrAriaProps({ label, 'aria-label': ariaLabel })}
      labelAdornment={labelAdornment}
      helperText={helperText}
      error={visibleError ?? errorMessage}
    >
      {fieldVariant === 'SelectOrBlockValueTemplate' ? (
        <BlockValueTemplateFieldWrapper
          fieldId={fieldId}
          fieldName={fieldName}
          aria-label={ariaLabelString}
          onModeChange={onSelectOrBlockValueTemplateModeChange}
        >
          <BlockInnerSelectField {...innerSelectFieldProps} variant="borderless" />
        </BlockValueTemplateFieldWrapper>
      ) : (
        <BlockInnerSelectField {...innerSelectFieldProps} />
      )}
    </FormField>
  );
}

export function getSourceBlockNameHelperText(dataName: string) {
  return `Choose upstream block that will provide the ${dataName}'s data.`;
}

export function ParentBlockSelectField(props: FieldProps<string>) {
  const { getAllParentNodeNames } = useReactFlowViewContext();
  const { selectedNodeId } = useReactFlowViewContext();
  if (!selectedNodeId) {
    return null;
  }
  const parentNodeNames = getAllParentNodeNames(selectedNodeId);

  return (
    <BlockSelectField
      {...props}
      options={parentNodeNames.map((name) => ({
        label: name,
        value: name,
      }))}
    />
  );
}
